import React, { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Server, Activity, Clock, CheckCircle, AlertCircle, Loader, FileText, Copy, Check, AlertTriangle, Globe, Wifi } from 'lucide-react';
import * as clusterService from '../services/clusterService';

const INIT_TIMEOUT_MINUTES = 15;

const NodeList = ({ cluster }) => {
  const [nodes, setNodes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (cluster.status !== 'UP' || (cluster.num_nodes || 1) <= 1) return;
    const fetch = async () => {
      try {
        const data = await clusterService.getClusterNodes(cluster.id);
        if (data.success && data.nodes) setNodes(data.nodes);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [cluster.id, cluster.status]);

  if ((cluster.num_nodes || 1) <= 1) return null;
  if (loading) return <div className="text-xs text-gray-400 py-2">Loading nodes...</div>;
  if (!nodes) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
        <Server size={14} />
        <span>Nodes ({nodes.length})</span>
      </h3>
      <div className="space-y-1.5">
        {nodes.map((node) => (
          <div key={node.index} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
              <div>
                <span className="text-sm font-medium">
                  {node.is_head ? 'Head Node' : `Worker ${node.index}`}
                </span>
                <span className="text-xs text-gray-400 ml-2">#{node.index}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              {node.external_ip && (
                <div className="flex items-center space-x-1" title="External IP">
                  <Globe size={10} />
                  <span className="font-mono">{node.external_ip}</span>
                </div>
              )}
              {node.internal_ip && (
                <div className="flex items-center space-x-1" title="Internal IP">
                  <Wifi size={10} />
                  <span className="font-mono">{node.internal_ip}</span>
                </div>
              )}
              {node.ssh_port && node.ssh_port !== 22 && (
                <span className="font-mono text-gray-400">:{node.ssh_port}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClusterDetailModal = ({ cluster, onClose, onTeardown }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef(null);

  const fetchDetails = async () => {
    try {
      const data = await clusterService.getClusterLogs(cluster.id);
      if (data.success) {
        setDetails(data);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // Auto-refresh while initializing
    if (cluster.status === 'INIT') {
      intervalRef.current = setInterval(fetchDetails, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cluster.id]);

  // Stop polling once no longer INIT
  useEffect(() => {
    if (details && details.status !== 'INIT' && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [details?.status]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'UP': return <CheckCircle size={18} className="text-green-500" />;
      case 'INIT': return <Loader size={18} className="text-yellow-500 animate-spin" />;
      case 'FAILED': return <AlertCircle size={18} className="text-red-500" />;
      case 'STOPPED': return <Clock size={18} className="text-gray-500" />;
      default: return <Server size={18} className="text-gray-400" />;
    }
  };

  const getOrchestratorStage = (orchestrator) => {
    if (!orchestrator) return null;
    const log = orchestrator.log || '';

    if (orchestrator.status === 'FAILED') return { label: 'Failed', color: 'text-red-600' };
    if (orchestrator.finished_at) return { label: 'Completed', color: 'text-green-600' };

    // Parse log to determine current stage
    if (log.includes('Running setup')) return { label: 'Running setup commands', color: 'text-blue-600' };
    if (log.includes('Pulling Docker image') || log.includes('docker pull')) return { label: 'Pulling Docker image', color: 'text-blue-600' };
    if (log.includes('Setting up SSH')) return { label: 'Setting up SSH access', color: 'text-blue-600' };
    if (log.includes('Launching on') || log.includes('Provisioning')) return { label: 'Provisioning instance', color: 'text-yellow-600' };
    if (log.includes('Searching for the cheapest') || log.includes('optimizer')) return { label: 'Finding cheapest option', color: 'text-purple-600' };
    if (orchestrator.started_at) return { label: 'Processing', color: 'text-yellow-600' };

    return { label: 'Queued', color: 'text-gray-600' };
  };

  const copyLog = () => {
    const log = details?.orchestrator?.log || '';
    navigator.clipboard.writeText(log);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (ts) => {
    if (!ts) return 'N/A';
    // SkyPilot returns Unix seconds; D1 returns ISO strings
    const d = typeof ts === 'number' && ts < 4102444800 ? new Date(ts * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">{cluster.cluster_name}</h2>
            <p className="text-sm text-purple-200">Launch Details</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); fetchDetails(); }}
              className="text-purple-200 hover:text-white transition-colors p-1"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="text-purple-200 hover:text-white transition-colors p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && !details && (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-purple-600 mr-2" size={24} />
              <span className="text-gray-600">Loading details...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}

          {details && (
            <>
              {/* Status Overview */}
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                {getStatusIcon(details.status)}
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">
                    {details.status === 'UP' && 'Running'}
                    {details.status === 'INIT' && 'Initializing'}
                    {details.status === 'FAILED' && 'Failed'}
                    {details.status === 'STOPPED' && 'Stopped'}
                    {details.status === 'DOWN' && 'Down'}
                  </div>
                  {details.orchestrator && (() => {
                    const stage = getOrchestratorStage(details.orchestrator);
                    return stage ? (
                      <span className={`text-sm ${stage.color}`}>{stage.label}</span>
                    ) : null;
                  })()}
                </div>
                {details.status === 'INIT' && (
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">Auto-refreshing</span>
                )}
              </div>

              {/* Stuck INIT Warning */}
              {details.status === 'INIT' && (() => {
                const created = new Date(cluster.created_at);
                const elapsedMin = (Date.now() - created.getTime()) / 1000 / 60;
                if (elapsedMin <= INIT_TIMEOUT_MINUTES) return null;
                return (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle size={18} className="text-red-500 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold text-red-800">Launch appears stuck</div>
                        <p className="text-sm text-red-600 mt-1">
                          This cluster has been initializing for {Math.floor(elapsedMin)} minutes.
                          The provisioning process may have failed silently (e.g., a hung SSH session during Docker image pull).
                        </p>
                        {onTeardown && (
                          <button
                            onClick={() => onTeardown(cluster.id, cluster.cluster_name)}
                            className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors inline-flex items-center space-x-1"
                          >
                            <span>Tear Down & Retry</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Error detail */}
              {details.orchestrator?.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle size={16} className="text-red-500" />
                    <span className="font-semibold text-red-800">Error</span>
                  </div>
                  <pre className="text-sm text-red-700 whitespace-pre-wrap font-mono bg-red-100 p-3 rounded overflow-x-auto max-h-40 overflow-y-auto">
                    {details.orchestrator.error}
                  </pre>
                </div>
              )}

              {/* Cluster Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">Cloud</div>
                  <div className="font-medium">{details.cloud_provider?.toUpperCase() || 'Auto'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">Region</div>
                  <div className="font-medium">{details.region || 'Auto'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">GPU</div>
                  <div className="font-medium">
                    {details.accelerator_count > 0 ? `${details.accelerator_count}x ${details.accelerator_type}` : 'None'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">Instance</div>
                  <div className="font-medium">{details.instance_type || 'Auto'}</div>
                </div>
                {details.endpoint && (
                  <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                    <div className="text-gray-500 text-xs mb-1">Endpoint</div>
                    <div className="font-mono font-medium text-sm">{details.endpoint}</div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                  <Clock size={14} />
                  <span>Timeline</span>
                </h3>
                <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span className="font-medium">{formatTime(details.created_at)}</span>
                  </div>
                  {details.orchestrator?.started_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Started</span>
                      <span className="font-medium">{formatTime(details.orchestrator.started_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Node List (multi-node only) */}
              <NodeList cluster={cluster} />

              {/* Orchestrator Log */}
              {details.orchestrator?.log && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <FileText size={14} />
                      <span>Orchestrator Log</span>
                    </h3>
                    <button
                      onClick={copyLog}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1 transition-colors"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <pre className="text-xs font-mono bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                    {details.orchestrator.log}
                  </pre>
                </div>
              )}

              {/* Task YAML */}
              {details.task_yaml && (
                <details className="group">
                  <summary className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-gray-900 flex items-center space-x-2">
                    <FileText size={14} />
                    <span>Task YAML</span>
                  </summary>
                  <pre className="mt-2 text-xs font-mono bg-gray-100 p-4 rounded-lg overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap text-gray-700">
                    {details.task_yaml}
                  </pre>
                </details>
              )}

              {/* Request ID */}
              {details.request_id && (
                <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                  Request ID: <span className="font-mono">{details.request_id}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClusterDetailModal;
